import { useDispatch, useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { green } from '@mui/material/colors';
import { VEHICLE_TYPE, FUEL_TYPE } from './contants';
import { selectVehicleInfo } from '../../redux-state/selectors';
import { DECODE_VIN } from '../../redux-state/fees/types';
import { useState } from 'react';
import CurrencyTextField from '../common/CurrencyField'


function VehicleInfoForm({ errors }) {
  const vehicleInfo = useSelector(selectVehicleInfo);
  const dispatch = useDispatch();

  const [localError, setLocalError] = useState('');

  const validateVIN = (vin) => /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.trim());

  const decodeVinCode = () => {
    if (!validateVIN(vehicleInfo.vinCode)) {
      setLocalError('Please enter a valid 17-character VIN');
      return;
    }

    setLocalError('');
    dispatch({
      type: DECODE_VIN,
      payload: { vin: vehicleInfo.vinCode } });
  };

  return (
    <div className="flex flex-col w-full">
      <div className="p-4 rounded-lg bg-white text-gray-700">
        <div className="text-lg font-bold">Vehicle Information</div>
        <div className="flex flex-wrap justify-between mt-2 text-sm">
          <div className="text-gray-600">
            Add correct vehicle details
          </div>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <TextField
            required
            label="VIN Number"
            variant="outlined"
            fullWidth
            value={vehicleInfo.vinCode}
            onChange={(e) => {
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'vinCode', value: e.target.value },
              });
            }}
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.vinCode || !!localError}
            helperText={errors?.vinCode || localError}
          />

          <Button
            onClick={decodeVinCode}
            variant="outlined"
            sx={{
              minWidth: '150px',
              height: '55px',
              color: '#229954',
              borderRadius: '10px',
              borderColor: '#229954',
              '&:hover': {
                backgroundColor: green[900],
                color: '#fff',
              },
            }}
          >
            Decode VIN
          </Button>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <FormControl fullWidth required error={errors?.vehicleType}>
            <InputLabel>Vehicle Type</InputLabel>
            <Select
              label="Vehicle Type"
              value={vehicleInfo.vehicleType}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_VEHICLE_INFO',
                  payload: { key: 'vehicleType', value: e.target.value },
                })
              }
            >
              {Object.keys(VEHICLE_TYPE).map((object, index) => (
                <MenuItem
                  key={index}
                  value={object}
                  className={object === "Multipurpose Passenger Vehicle" ? "hidden-important" : ""}
                >
                  {VEHICLE_TYPE[object]}
                </MenuItem>
              ))}
            </Select>
            {errors?.vehicleType && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.vehicleType}</div>
            )}
          </FormControl>

          <FormControl fullWidth required error={errors?.fuelType}>
            <InputLabel>Fuel Type</InputLabel>
            <Select
              label="Fuel Type"
              value={vehicleInfo.fuelType}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_VEHICLE_INFO',
                  payload: { key: 'fuelType', value: e.target.value },
                })
              }
            >
              {Object.keys(FUEL_TYPE).map((key, index) => (
                <MenuItem key={index} value={key}>
                  {FUEL_TYPE[key]}
                </MenuItem>
              ))}
            </Select>
            {errors?.fuelType && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.fuelType}</div>
            )}
          </FormControl>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <TextField
            required
            type="number"
            label="Vehicle Weight"
            variant="outlined"
            fullWidth
            value={vehicleInfo.vehicleWeight}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'vehicleWeight', value: e.target.value },
              })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Select
                    value="lbs"
                    variant="standard"
                    disableUnderline
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="lbs">lbs</MenuItem>
                  </Select>
                </InputAdornment>
              ),
              style: { borderRadius: '10px' },
            }}
            error={!!errors?.vehicleWeight}
            helperText={errors?.vehicleWeight}
          />

          <TextField
            label="GVW"
            variant="outlined"
            fullWidth
            value={vehicleInfo.gvw}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'gvw', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.gvw}
            helperText={errors?.gvw}
          />
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4 pb-4">
          <CurrencyTextField
            label="MSRP"
            name="msrp"
            required
            value={vehicleInfo.msrp ?? ''} // keep coSntrolled
            onChange={(num) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'msrp', value: num }, // <-- num is a Number or '' when cleared
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.msrp}
            helperText={errors?.msrp}
          />

          <TextField
            required
            label="Model Year"
            type="number"
            variant="outlined"
            fullWidth
            value={vehicleInfo.modelYear}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'modelYear', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.modelYear}
            helperText={errors?.modelYear}
          />
        </div>
      </div>
    </div>
  );
}

export default VehicleInfoForm;
